import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { SteveCategory } from 'models/reward-cafeteria/SteveCategory';
import classnames from 'classnames';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

export type CafeteriaCategoryListProps = {
	categoryList?: SteveCategory[];
	selectedCategoryId?: string | null;
	onCategoryChange: (categoryId: string | null) => void;
};

const shortListLength = 7;

export const CafeteriaCategoryList = ({
	categoryList,
	selectedCategoryId,
	onCategoryChange,
}: CafeteriaCategoryListProps) => {
	const [isListExpanded, setIsListExpanded] = useState(false);
	const [selectedCategoryList, setSelectedCategoryList] = useState<SteveCategory | null>(null);
	const selectCategory = useCallback(
		(category: SteveCategory | null) => {
			if (selectedCategoryId === (category?.id || null)) {
				return;
			}

			onCategoryChange(category?.id || null);

			if (!category || !!category?.children.length) {
				setSelectedCategoryList(category);
				setIsListExpanded(false);
			}
		},
		[onCategoryChange, setSelectedCategoryList, setIsListExpanded, selectedCategoryId]
	);
	const categories = selectedCategoryList?.children || categoryList;
	const list = useMemo(() => {
		if (!categories) {
			return [];
		}

		if (isListExpanded) {
			return categories;
		}

		return categories?.slice(0, shortListLength);
	}, [categories, isListExpanded]);

	useEffect(() => {
		if (categoryList && selectedCategoryId) {
			let category: SteveCategory | null = null;

			categoryList.forEach(categoryItem => {
				if (categoryItem.id === selectedCategoryId) {
					category = categoryItem;
					setSelectedCategoryList(categoryItem);
				} else if (categoryItem.children && categoryItem.children.length) {
					categoryItem.children.forEach(childrenItem => {
						if (childrenItem.id === selectedCategoryId) {
							category = childrenItem;
							setSelectedCategoryList(categoryItem);
						}
					});
				}
			});

			if (!category) {
				onCategoryChange(null);
			}
		}
	}, [categoryList, selectedCategoryId]);

	if (!categoryList || !categoryList.length) {
		return null;
	}

	return (
		<div className="d-flex flex-column cafeteria__filters__categories">
			<div>
				<strong className="fs-5 fs-sm-6">
					<FormattedMessage id="cafeteria.filter.category.title" />
				</strong>
			</div>
			{!!selectedCategoryList && (
				<div className="d-flex flex-column cafeteria__filters__categories__go-back">
					<div>
						<strong>{selectedCategoryList.name}</strong>
					</div>
					<div className="cafeteria__filters__categories__go-back__cta" onClick={() => selectCategory(null)}>
						<FormattedHTMLMessage id="cafeteria.filter.category.goBack" />
					</div>
				</div>
			)}
			<div className="d-flex flex-column cafeteria__filters__categories__list">
				{list.map((category, index) => (
					<Fragment key={category.id}>
						<div
							className={classnames('cafeteria__filters__categories__item', {
								'cafeteria__filters__categories__item--selected': selectedCategoryId === category.id,
							})}
							onClick={() => {
								selectCategory(category);
							}}
						>
							{category.name}
						</div>
						{index < list?.length - 1 && (
							<div className="cafeteria__filters__categories__list__separator"></div>
						)}
					</Fragment>
				))}
			</div>
			{categories && !isListExpanded && categories?.length > shortListLength && (
				<div className="cafeteria__filters__categories__show-more" onClick={() => setIsListExpanded(true)}>
					<FormattedMessage id="cafeteria.filter.category.more" />
				</div>
			)}
		</div>
	);
};
